import OlGeolocation from 'ol/Geolocation';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import { ref, watch } from 'vue';

import { useMap } from './useMap';

const tracking = ref(false);
let geolocation: OlGeolocation | null = null;
let positionLayer: VectorLayer | null = null;
let accuracyLayer: VectorLayer | null = null;

/**
 * Composable for browser geolocation — mirrors frontend-next's geolocationButton.js.
 * Shows a green dot at the user's location and optionally an accuracy circle.
 */
export function useGeolocation() {
  const { map } = useMap();

  const toggleGeolocation = () => {
    tracking.value = !tracking.value;
  };

  watch(tracking, (isTracking) => {
    if (!map.value) return;

    if (isTracking) {
      startTracking();
    } else {
      stopTracking();
    }
  });

  const startTracking = () => {
    if (!map.value) return;

    geolocation = new OlGeolocation({
      trackingOptions: { enableHighAccuracy: true },
      projection: map.value.getView().getProjection(),
    });

    geolocation.setTracking(true);

    // Position marker — green dot like WFS
    const positionFeature = new Feature();
    positionFeature.setStyle(
      new Style({
        image: new CircleStyle({
          radius: 6,
          fill: new Fill({ color: '#42F1A7' }),
          stroke: new Stroke({ color: '#fff', width: 2 }),
        }),
      }),
    );

    // Accuracy circle
    const accuracyFeature = new Feature();

    positionLayer = new VectorLayer({
      source: new VectorSource({ features: [positionFeature] }),
      zIndex: 999,
    });

    accuracyLayer = new VectorLayer({
      source: new VectorSource({ features: [accuracyFeature] }),
      zIndex: 998,
      style: new Style({
        fill: new Fill({ color: 'rgba(66, 241, 167, 0.15)' }),
        stroke: new Stroke({ color: 'rgba(66, 241, 167, 0.4)', width: 1 }),
      }),
    });

    map.value.addLayer(accuracyLayer);
    map.value.addLayer(positionLayer);

    geolocation.on('change:position', () => {
      const coords = geolocation!.getPosition();
      if (coords) {
        positionFeature.setGeometry(new Point(coords));
        map.value?.getView().animate({
          center: coords,
          zoom: Math.max(map.value.getView().getZoom() ?? 0, 14),
          duration: 500,
        });
      }
    });

    geolocation.on('change:accuracyGeometry', () => {
      const geom = geolocation!.getAccuracyGeometry();
      if (geom) {
        accuracyFeature.setGeometry(geom);
      }
    });

    geolocation.on('error', () => {
      tracking.value = false;
      stopTracking();
    });
  };

  const stopTracking = () => {
    if (geolocation) {
      geolocation.setTracking(false);
      geolocation = null;
    }
    if (positionLayer && map.value) {
      map.value.removeLayer(positionLayer);
      positionLayer = null;
    }
    if (accuracyLayer && map.value) {
      map.value.removeLayer(accuracyLayer);
      accuracyLayer = null;
    }
  };

  return {
    tracking,
    toggleGeolocation,
  };
}
